import {
  useState, useEffect, useMemo, useRef,
} from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";
import {
  TrendingUp, TrendingDown, Users, DollarSign, Activity, Clock, Loader2,
  Calendar, ArrowRightLeft, BarChart3, Stethoscope, Settings2, X,
  GripVertical, Plus, Type, Hash, TrendingUp as TrendingUpIcon,
} from "lucide-react";

const MONTHS_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

interface VersionInfo {
  version: string;
  upload_count: number;
  total_records: number;
  last_report_date: string;
  last_report_day?: number;
  last_upload: string;
}

interface KpiMetric {
  value: number;
  delta: number | null;
}

type SpecialtyOption = string | "All";

type ViewMode = "current" | "actuals";

interface WidgetConfig {
  id: string;
  type: "kpi" | "summary" | "chart" | "specialties" | "units";
  title?: string;
  metric?: string;
  sheet?: string;
  period_type?: "month" | "day" | "total";
  entity_level?: "specialty" | "unit" | "doctor" | "group";
  prefix?: string;
  suffix?: string;
  mode?: "sum" | "avg";
}

const ALL_SPECIALTIES_LABEL = "All specialties";
const DEFAULT_SPECIALTIES: SpecialtyOption[] = [
  "Cardiologia",
  "Cardiologia Pediátrica",
  "Cirurgia",
  "Pneumologia",
  "Cirurgia Plástica",
  "Imunoalergologia",
  "Cirurgia Vascular",
];

const DEFAULT_WIDGETS: WidgetConfig[] = [
  { id: "builtin-kpis", type: "kpi" },
  { id: "builtin-chart", type: "chart" },
  { id: "builtin-summary", type: "summary" },
  { id: "builtin-specialties", type: "specialties" },
  { id: "builtin-units", type: "units" },
];

const KPI_DEFS = [
  { key: "Nr Cons N // Δ Orç", label: "Consultations", icon: Users, prefix: "", sheet: "Consulta" },
  { key: "Valor Fat N // Δ Orç", label: "Billing", icon: DollarSign, prefix: "€", suffix: "k", sheet: "Faturação por Especialidade" },
  { key: "Tx Ocupação // Δ N-1", label: "Occupancy Rate", icon: Activity, prefix: "", suffix: "%", mode: "avg", sheet: "Consulta" },
  { key: "Nr 1ª Consultas N", label: "First Consultations", icon: Clock, prefix: "", sheet: "Consulta" },
];

const CHART_PALETTE = ["#005F71", "#217D84", "#3A9A9F", "#52B7BB", "#6BD4D7"];

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [specialty, setSpecialty] = useState<SpecialtyOption>("All");
  const [availableSpecialties, setAvailableSpecialties] = useState<SpecialtyOption[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("current");
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState<Record<string, Record<string, KpiMetric>>>({});
  const [monthlyData, setMonthlyData] = useState<any[]>([]);
  const [dailyData, setDailyData] = useState<any[]>([]);
  const [unitData, setUnitData] = useState<any[]>([]);
  const [specialtyData, setSpecialtyData] = useState<any[]>([]);
  const [unitMonthlyData, setUnitMonthlyData] = useState<{ name: string; monthly: Record<string, number> }[]>([]);
  const [specialtyMonthlyData, setSpecialtyMonthlyData] = useState<{ name: string; monthly: Record<string, number> }[]>([]);
  const [customizing, setCustomizing] = useState(false);
  const [widgets, setWidgets] = useState<WidgetConfig[]>(() => {
    try {
      const saved = JSON.parse(user?.dashboard_layout || "null");
      return Array.isArray(saved) && saved.length ? saved : DEFAULT_WIDGETS;
    } catch {
      return DEFAULT_WIDGETS;
    }
  });
  const [dataFilterOptions, setDataFilterOptions] = useState<{ sheets: string[]; metrics: string[] }>({ sheets: [], metrics: [] });
  const [newWidget, setNewWidget] = useState<Partial<WidgetConfig>>({ type: "kpi", period_type: "month", entity_level: "specialty" });
  const [reportLoading, setReportLoading] = useState(false);

  const [dailyCutoffDay, setDailyCutoffDay] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem("dashboardDailyCutoff");
      return saved ? parseInt(saved, 10) : null;
    } catch {
      return null;
    }
  });

  const effectiveDailyCutoff = dailyCutoffDay ?? version?.last_report_day ?? 31;

  // Persist manual daily cutoff override.
  useEffect(() => {
    if (dailyCutoffDay != null) {
      localStorage.setItem("dashboardDailyCutoff", String(dailyCutoffDay));
    } else {
      localStorage.removeItem("dashboardDailyCutoff");
    }
  }, [dailyCutoffDay]);

  useEffect(() => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    Promise.all([
      fetch("/api/version").then((r) => r.json()),
      fetch("/api/data/filters?report_type=daily_activity", { headers }).then((r) => r.json()).catch(() => null),
    ])
      .then(([v, filters]) => {
        setVersion(v);
        setAvailableSpecialties(DEFAULT_SPECIALTIES);
      })
      .catch(() => {
        setVersion(null);
        setAvailableSpecialties(DEFAULT_SPECIALTIES);
      })
      .finally(() => setLoading(false));
  }, []);

  const activeSpecs = specialty === "All"
    ? (availableSpecialties.length > 0 ? availableSpecialties : DEFAULT_SPECIALTIES)
    : [specialty];

  // KPIs
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        Promise.all(
          KPI_DEFS.map((def) =>
            fetch(
              `/api/data/timeseries?sheet=${encodeURIComponent(def.sheet)}&metric=${encodeURIComponent(def.key)}&period_type=month&entity_level=specialty&specialty=${spec}&unit=__none__`,
              { headers }
            ).then((r) => r.json())
          )
        ).then((results) => ({ spec, results }))
      )
    ).then((allResults) => {
      const byMetric: Record<string, Record<string, KpiMetric>> = {};
      KPI_DEFS.forEach((def, defIdx) => {
        byMetric[def.key] = {};
        allResults.forEach(({ results }) => {
          (results[defIdx] || []).forEach((d: any) => {
            if (d.value != null) {
              const existing = byMetric[def.key][d.period_label];
              const val = d.value_numeric ?? d.value;
              if (existing) {
                existing.value += val;
              } else {
                byMetric[def.key][d.period_label] = { value: val, delta: d.delta_pct };
              }
            }
          });
        });
      });
      setKpiData(byMetric);
    }).catch(() => setKpiData({}));
  }, [specialty]);

  // Monthly consultations trend
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=Consulta&metric=${encodeURIComponent("Nr Cons N // Δ Orç")}&period_type=month&entity_level=specialty&specialty=${spec}&unit=__none__`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const byPeriod: Record<string, { month: string; value: number; order: number }> = {};
      results.forEach((data) => {
        data.forEach((d: any) => {
          if (d.value != null) {
            const order = d.period_order || MONTHS_PT.indexOf(d.period_label);
            if (!byPeriod[d.period_label]) {
              byPeriod[d.period_label] = { month: d.period_label, value: 0, order };
            }
            byPeriod[d.period_label].value += d.value;
          }
        });
      });
      setMonthlyData(Object.values(byPeriod).sort((a, b) => a.order - b.order));
    }).catch(() => setMonthlyData([]));
  }, [specialty]);

  // Daily consultations for current month mode
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=Consulta&metric=${encodeURIComponent("Nr Cons N // Δ Orç")}&period_type=day&entity_level=specialty&specialty=${spec}&unit=__none__`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const byDay: Record<string, { day: string; value: number; order: number }> = {};
      results.forEach((data) => {
        data.forEach((d: any) => {
          if (d.value != null) {
            const day = d.period_label;
            if (!byDay[day]) {
              byDay[day] = { day, value: 0, order: parseInt(day) };
            }
            byDay[day].value += d.value;
          }
        });
      });
      setDailyData(Object.values(byDay).sort((a, b) => a.order - b.order));
    }).catch(() => setDailyData([]));
  }, [specialty]);

  // Unit totals
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    fetch(
      `/api/data/timeseries?sheet=Consulta&metric=${encodeURIComponent("Nr Cons N // Δ Orç")}&period_type=total&entity_level=unit`,
      { headers }
    )
      .then((r) => r.json())
      .then((data) => {
        setUnitData(
          data
            .filter((d: any) => d.value != null && d.value > 0)
            .map((d: any) => ({ name: d.entity_name, value: d.value }))
            .sort((a: any, b: any) => b.value - a.value)
            .slice(0, 15)
        );
      })
      .catch(() => setUnitData([]));
  }, [specialty]);

  // Unit monthly data for breakdown
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    fetch(
      `/api/data/timeseries?sheet=Consulta&metric=${encodeURIComponent("Nr Cons N // Δ Orç")}&period_type=month&entity_level=unit`,
      { headers }
    )
      .then((r) => r.json())
      .then((data) => {
        const byUnit: Record<string, { name: string; monthly: Record<string, number> }> = {};
        data.forEach((d: any) => {
          if (!byUnit[d.entity_name]) {
            byUnit[d.entity_name] = { name: d.entity_name, monthly: {} };
          }
          byUnit[d.entity_name].monthly[d.period_label] = d.value_numeric ?? d.value ?? 0;
        });
        setUnitMonthlyData(Object.values(byUnit));
      })
      .catch(() => setUnitMonthlyData([]));
  }, [specialty]);

  // Specialty monthly data for breakdown
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=Consulta&metric=${encodeURIComponent("Nr Cons N // Δ Orç")}&period_type=month&entity_level=specialty&specialty=${spec}&unit=__none__`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const bySpec: Record<string, { name: string; monthly: Record<string, number> }> = {};
      results.forEach((series) => {
        series.forEach((d: any) => {
          if (!bySpec[d.entity_name]) {
            bySpec[d.entity_name] = { name: d.entity_name, monthly: {} };
          }
          bySpec[d.entity_name].monthly[d.period_label] = d.value_numeric ?? d.value ?? 0;
        });
      });
      setSpecialtyMonthlyData(Object.values(bySpec));
    }).catch(() => setSpecialtyMonthlyData([]));
  }, [specialty]);

  // Specialty totals
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=Consulta&metric=${encodeURIComponent("Nr Cons N // Δ Orç")}&period_type=total&entity_level=specialty&specialty=${spec}&unit=__none__`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const combined: { name: string; value: number }[] = [];
      let total = 0;
      results.forEach((data) => {
        data.forEach((d: any) => {
          if (d.value != null && d.value > 0) {
            combined.push({ name: d.entity_name, value: d.value });
            total += d.value;
          }
        });
      });
      if (specialty === "All" && combined.length > 1) {
        combined.push({ name: "Combined", value: total });
      }
      setSpecialtyData(combined.sort((a, b) => b.value - a.value));
    }).catch(() => setSpecialtyData([]));
  }, [specialty]);

  const saveWidgets = async (next: WidgetConfig[]) => {
    setWidgets(next);
    try {
      const updated = await api.updateMe({ dashboard_layout: JSON.stringify(next) });
      setUser(updated);
    } catch (e) {
      console.error("Failed to save dashboard layout", e);
    }
  };

  const removeWidget = (id: string) => {
    saveWidgets(widgets.filter((w) => w.id !== id));
  };

  const moveWidget = (id: string, direction: -1 | 1) => {
    const idx = widgets.findIndex((w) => w.id === id);
    if (idx < 0) return;
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= widgets.length) return;
    const next = [...widgets];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    saveWidgets(next);
  };

  const addCustomWidget = () => {
    if (!newWidget.metric || !newWidget.sheet) return;
    const title = newWidget.title || newWidget.metric;
    const config: WidgetConfig = {
      id: `custom-${Date.now()}`,
      type: newWidget.type || "kpi",
      title,
      metric: newWidget.metric,
      sheet: newWidget.sheet,
      period_type: newWidget.period_type || "month",
      entity_level: newWidget.entity_level || "specialty",
      prefix: newWidget.prefix || "",
      suffix: newWidget.suffix || "",
      mode: newWidget.mode || "sum",
    };
    saveWidgets([...widgets, config]);
    setNewWidget({ type: "kpi", period_type: "month", entity_level: "specialty" });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-trofa-700" />
      </div>
    );
  }

  const reportMonth = version?.last_report_date
    ? parseInt(version.last_report_date.split("/")[1])
    : null;
  const reportDate = version?.last_report_date || "-";
  const currentMonthIndex = reportMonth ? reportMonth - 1 : -1;
  const currentMonth = currentMonthIndex >= 0 ? MONTHS_PT[currentMonthIndex] : null;
  const currentDayOfMonth = new Date().getDate();
  const reportDayOrToday = version?.last_report_day
    ? Math.min(version.last_report_day, currentDayOfMonth)
    : currentDayOfMonth;

  const actualsMonths = currentMonthIndex >= 0
    ? MONTHS_PT.slice(0, currentMonthIndex + 1)
    : MONTHS_PT.slice(0, 6);

  const displayReportRange = viewMode === "actuals"
    ? `2026/01 - ${reportDate}`
    : `${reportDate} (1-${effectiveDailyCutoff})`;

  const currentMonthValue = currentMonth
    ? monthlyData.find((d) => d.month === currentMonth)?.value || 0
    : 0;
  const prevMonthValue = currentMonthIndex > 0
    ? monthlyData.find((d) => d.month === MONTHS_PT[currentMonthIndex - 1])?.value || 0
    : 0;

  const actualsTotal = monthlyData
    .filter((d) => {
      const idx = MONTHS_PT.indexOf(d.month);
      return idx >= 0 && idx <= 5;
    })
    .reduce((sum, d) => sum + (d.value || 0), 0);

  const visibleUnitData = (() => {
    return unitMonthlyData
      .map((u) => ({
        name: u.name,
        value: viewMode === "current" ? (u.monthly[currentMonth || "Jul"] || 0) : actualsMonths.reduce((sum, m) => sum + (u.monthly[m] || 0), 0),
      }))
      .filter((u) => u.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 15);
  })();

  const visibleSpecialtyData = (() => {
    const rows = specialtyMonthlyData.map((s) => ({
      name: s.name,
      value: viewMode === "current" ? (s.monthly[currentMonth || "Jul"] || 0) : actualsMonths.reduce((sum, m) => sum + (s.monthly[m] || 0), 0),
    }));
    if (specialty === "All" && rows.length > 1) {
      const combined = rows.reduce((sum, r) => sum + r.value, 0);
      rows.push({ name: "Combined", value: combined });
    }
    return rows
      .filter((s) => s.value > 0)
      .sort((a, b) => b.value - a.value);
  })();

  const actualsChartData = monthlyData.filter((d) => {
    const idx = MONTHS_PT.indexOf(d.month);
    return idx >= 0 && idx <= currentMonthIndex;
  });

  const filteredDailyData = dailyData.filter((d) => parseInt(d.day) <= effectiveDailyCutoff);

  const chartData = viewMode === "current" ? filteredDailyData : actualsChartData;

  const chartTotal = viewMode === "current"
    ? filteredDailyData.reduce((sum, d) => sum + (d.value || 0), 0)
    : actualsTotal;

  const chartXKey = viewMode === "current" ? "day" : "month";
  const chartTitle = viewMode === "current"
    ? `Daily consultations in ${currentMonth || "current month"}`
    : `Actual months YTD · total ${chartTotal.toLocaleString()}`;

  const renderKpis = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {KPI_DEFS.map((card) => {
          const monthly = kpiData[card.key] || {};
          let value = 0;
          let delta: number | null = null;
          if (viewMode === "current") {
            const currentMonthMetric = currentMonth ? monthly[currentMonth] : null;
            value = currentMonthMetric?.value || 0;
            delta = currentMonthMetric?.delta ?? null;
          } else {
            const actuals = Object.entries(monthly)
              .filter(([month]) => {
                const idx = MONTHS_PT.indexOf(month);
                return idx >= 0 && idx <= 5;
              })
              .map(([, v]) => v.value || 0);
            if (actuals.length === 0) {
              value = 0;
            } else if ((card as any).mode === "avg") {
              value = actuals.reduce((sum, v) => sum + v, 0) / actuals.length;
            } else {
              value = actuals.reduce((sum, v) => sum + v, 0);
            }
          }
          const isPositive = delta != null && delta >= 0;
          const displayValue =
            (card as any).mode === "avg"
              ? `${Math.round(value)}%`
              : `${card.prefix}${value.toLocaleString()}${card.suffix || ""}`;
          return (
            <div key={card.key} className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm transition-shadow">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-trofa-50">
                  <card.icon className="w-5 h-5 text-trofa-700" />
                </div>
                <span className="text-sm text-slate-500">{card.label}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-slate-900">{displayValue}</span>
                {delta != null && (
                  <span className={`flex items-center text-sm font-medium ${isPositive ? "text-green-600" : "text-red-500"}`}>
                    {isPositive ? <TrendingUp className="w-3.5 h-3.5 mr-0.5" /> : <TrendingDown className="w-3.5 h-3.5 mr-0.5" />}
                    {delta > 0 ? "+" : ""}{delta}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSummary = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {viewMode === "current" ? `Current Month (${currentMonth})` : "Actuals (YTD)"}
          </p>
        </div>
        <p className="text-3xl font-bold text-slate-900">
          {(viewMode === "current" ? currentMonthValue : actualsTotal).toLocaleString()}
        </p>
        <p className="text-sm text-slate-500 mt-1">
          {viewMode === "current" ? "consultations · partial month up to report date" : "consultations · sum of YTD actual months"}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <ArrowRightLeft className="w-4 h-4 text-slate-400" />
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {viewMode === "current" ? "vs Previous Month" : "vs YTD"}
          </p>
        </div>
        <div className="flex items-baseline gap-2">
          <p className="text-3xl font-bold text-slate-900">
            {(viewMode === "current" ? prevMonthValue : currentMonthValue).toLocaleString()}
          </p>
          <ComparisonBadge viewMode={viewMode} currentValue={currentMonthValue} actualsTotal={actualsTotal} prevValue={prevMonthValue} />
        </div>
        <p className="text-sm text-slate-500 mt-1">
          {viewMode === "current" ? `${MONTHS_PT[currentMonthIndex - 1] || "-"} baseline` : "current month"}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-slate-400" />
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            {viewMode === "current" ? "Current Month Avg / Day" : "YTD Average"}
          </p>
        </div>
        <p className="text-3xl font-bold text-slate-900">
          {viewMode === "current"
            ? Math.round(chartData.length ? chartTotal / chartData.length : 0).toLocaleString()
            : Math.round(monthlyData.length ? actualsTotal / Math.max(1, actualsMonths.length) : 0).toLocaleString()}
        </p>
        <p className="text-sm text-slate-500 mt-1">
          {viewMode === "current" ? "avg consultations / day" : "avg consultations / month"}
        </p>
      </div>
    </div>
  );

  const renderChart = () => (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            Consultations by {viewMode === "current" ? "Day" : "Month"} ({specialty === "All" ? ALL_SPECIALTIES_LABEL : specialty})
          </h3>
          <p className="text-xs text-slate-500">{chartTitle}</p>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full border ${
          viewMode === "current" ? "bg-trofa-50 border-trofa-200 text-trofa-700" : "bg-green-50 border-green-200 text-green-700"
        }`}>
          {viewMode === "current" ? "Current" : "Actuals"}
        </span>
      </div>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey={chartXKey} stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
              formatter={(value: any) => [Number(value).toLocaleString(), "Consultations"]}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]} fill="#005F71" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-sm text-slate-400 py-8 text-center">No chart data available.</p>
      )}
    </div>
  );

  const renderSpecialties = () => {
    const maxValue = Math.max(...visibleSpecialtyData.map((s) => s.value || 0), 1);
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Stethoscope className="w-4 h-4 text-slate-400" />
          Consultations by Specialty ({viewMode === "current" ? currentMonth || "Current" : "YTD"})
        </h3>
        {visibleSpecialtyData.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center">No specialty data available.</p>
        ) : (
          <div className="space-y-3">
            {visibleSpecialtyData.map((s) => (
              <div key={s.name} className="flex items-center justify-between">
                <span className={`text-sm ${s.name === "Combined" ? "font-semibold text-trofa-800" : "text-slate-700"}`}>
                  {s.name}
                  {s.name === "Combined" && <span className="ml-1 text-xs font-normal text-slate-500">(All)</span>}
                </span>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${s.name === "Combined" ? "bg-trofa-800" : "bg-trofa-600"}`}
                      style={{ width: `${Math.min(100, (s.value / maxValue) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-slate-900 w-16 text-right">{s.value.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderUnits = () => (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-slate-400" />
        Top Health Units ({viewMode === "current" ? currentMonth || "Current" : "YTD"})
      </h3>
      <ResponsiveContainer width="100%" height={Math.max(280, visibleUnitData.length * 28)}>
        <BarChart data={visibleUnitData} layout="vertical" margin={{ left: 100 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
          <XAxis type="number" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} width={100} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
          <Bar dataKey="value" fill="#217D84" radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderWidget = (widget: WidgetConfig, index: number) => {
    if (widget.type === "kpi" && widget.id === "builtin-kpis") return renderKpis();
    if (widget.type === "summary" && widget.id === "builtin-summary") return renderSummary();
    if (widget.type === "chart" && widget.id === "builtin-chart") return renderChart();
    if (widget.type === "specialties" && widget.id === "builtin-specialties") return renderSpecialties();
    if (widget.type === "units" && widget.id === "builtin-units") return renderUnits();
    return <CustomWidget key={widget.id} config={widget} specialty={specialty} viewMode={viewMode} reportMonth={reportMonth} onRemove={() => removeWidget(widget.id)} />;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-slate-500 mt-1">
            Report: {displayReportRange}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setCustomizing(!customizing)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:border-trofa-400 transition-colors"
          >
            <Settings2 className="w-4 h-4" />
            Customize
          </button>
          <button
            onClick={async () => {
              setReportLoading(true);
              const win = window.open("", "_blank");
              if (!win) {
                alert("Could not open report. Please allow popups for this site.");
                setReportLoading(false);
                return;
              }
              try {
                const spec = specialty === "All" ? "" : specialty;
                const html = await api.getDirectorBriefStrategyHtml(spec);
                const blob = new Blob([html], { type: "text/html" });
                const url = URL.createObjectURL(blob);
                win.location.href = url;
              } catch (err) {
                console.error(err);
                alert("Failed to generate report. Check console.");
                win.close();
              } finally {
                setReportLoading(false);
              }
            }}
            disabled={reportLoading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-trofa-700 rounded-lg hover:bg-trofa-800 transition-colors disabled:opacity-70"
          >
            {reportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
            Director Brief
          </button>
          <div className="flex items-center bg-white border border-slate-200 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode("current")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === "current" ? "bg-trofa-700 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Current
            </button>
            <button
              onClick={() => setViewMode("actuals")}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                viewMode === "actuals" ? "bg-trofa-700 text-white" : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              Actuals (YTD)
            </button>
          </div>
          {viewMode === "current" && (
            <div className="flex items-center gap-2">
              <select
                value={effectiveDailyCutoff}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setDailyCutoffDay(val === reportDayOrToday ? null : val);
                }}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
                title="Daily chart cutoff"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>Days 1–{d}</option>
                ))}
              </select>
            </div>
          )}
          <select
            value={specialty}
            onChange={(e) => setSpecialty(e.target.value as SpecialtyOption)}
            className="border border-slate-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
          >
            <option value="All">{ALL_SPECIALTIES_LABEL}</option>
            {availableSpecialties.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {customizing && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Customize Dashboard</h3>
            <button onClick={() => setCustomizing(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-3">Reorder, remove or add data widgets below.</p>

          <div className="space-y-2 mb-4">
            {widgets.map((w, idx) => (
              <div key={w.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <div className="flex items-center gap-3">
                  <GripVertical className="w-4 h-4 text-slate-300" />
                  <span className="text-sm text-slate-700">{w.title || w.type}</span>
                  {w.metric && <span className="text-xs text-slate-400">{w.sheet} · {w.metric}</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button disabled={idx <= 0} onClick={() => moveWidget(w.id, -1)} className="p-1 text-slate-400 hover:text-trofa-700 disabled:opacity-30">↑</button>
                  <button disabled={idx >= widgets.length - 1} onClick={() => moveWidget(w.id, 1)} className="p-1 text-slate-400 hover:text-trofa-700 disabled:opacity-30">↓</button>
                  <button onClick={() => removeWidget(w.id)} className="ml-2 p-1 text-red-400 hover:text-red-600" title="Remove">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Add new widget
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <input
                type="text"
                placeholder="Widget title"
                value={newWidget.title || ""}
                onChange={(e) => setNewWidget({ ...newWidget, title: e.target.value })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
              <select
                value={newWidget.type}
                onChange={(e) => setNewWidget({ ...newWidget, type: e.target.value as any })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="kpi">Stat card</option>
                <option value="chart">Bar chart</option>
              </select>
              <select
                value={newWidget.sheet || ""}
                onChange={(e) => setNewWidget({ ...newWidget, sheet: e.target.value })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select sheet…</option>
                {dataFilterOptions.sheets.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <select
                value={newWidget.metric || ""}
                onChange={(e) => setNewWidget({ ...newWidget, metric: e.target.value })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select metric…</option>
                {dataFilterOptions.metrics.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <select
                value={newWidget.period_type || ""}
                onChange={(e) => setNewWidget({ ...newWidget, period_type: e.target.value as any })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="month">Monthly</option>
                <option value="day">Daily</option>
                <option value="total">Total</option>
              </select>
              <select
                value={newWidget.entity_level || ""}
                onChange={(e) => setNewWidget({ ...newWidget, entity_level: e.target.value as any })}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="specialty">Specialty</option>
                <option value="unit">Unit</option>
                <option value="doctor">Doctor</option>
                <option value="group">Group</option>
              </select>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Prefix e.g. €"
                  value={newWidget.prefix || ""}
                  onChange={(e) => setNewWidget({ ...newWidget, prefix: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Suffix e.g. k"
                  value={newWidget.suffix || ""}
                  onChange={(e) => setNewWidget({ ...newWidget, suffix: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <button
              onClick={addCustomWidget}
              disabled={!newWidget.metric || !newWidget.sheet}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-trofa-700 rounded-lg hover:bg-trofa-800 disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Add widget
            </button>
          </div>
        </div>
      )}

      {viewMode === "current" && reportMonth && (
        <div className="mb-4 px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Showing the report month ({reportDate}) with daily breakdown up to day {effectiveDailyCutoff}. Use the dropdown to adjust the range.
        </div>
      )}

      {widgets.map((widget, idx) => (
        <div key={widget.id} className={`relative group ${widget.type === "specialties" || widget.type === "units" ? "" : ""}`}>
          {customizing && (
            <div className="absolute -top-2 -right-2 z-10 flex gap-1">
              <button onClick={() => moveWidget(widget.id, -1)} disabled={idx <= 0} className="p-1 bg-white border rounded shadow text-xs disabled:opacity-30">↑</button>
              <button onClick={() => moveWidget(widget.id, 1)} disabled={idx >= widgets.length - 1} className="p-1 bg-white border rounded shadow text-xs disabled:opacity-30">↓</button>
              <button onClick={() => removeWidget(widget.id)} className="p-1 bg-white border rounded shadow text-xs text-red-600">×</button>
            </div>
          )}
          {renderWidget(widget, idx)}
        </div>
      ))}
    </div>
  );
}

function CustomWidget({ config, specialty, viewMode, reportMonth, onRemove }: any) {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const activeSpecs = specialty === "All"
    ? (availableSpecialties.length > 0 ? availableSpecialties : DEFAULT_SPECIALTIES)
    : [specialty];

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    setLoading(true);

    const periodType = viewMode === "current" && config.period_type === "month" ? "day" : config.period_type;

    Promise.all(
      activeSpecs.map((spec) =>
        fetch(
          `/api/data/timeseries?sheet=${encodeURIComponent(config.sheet)}&metric=${encodeURIComponent(config.metric)}&period_type=${periodType}&entity_level=${config.entity_level || "specialty"}&specialty=${spec}&unit=__none__`,
          { headers }
        ).then((r) => r.json())
      )
    ).then((results) => {
      const merged: Record<string, any> = {};
      results.forEach((series) => {
        series.forEach((d: any) => {
          const label = d.period_label;
          if (!merged[label]) {
            merged[label] = { label, value: 0, order: d.period_order ?? 0 };
          }
          merged[label].value += d.value_numeric ?? d.value ?? 0;
        });
      });
      setData(Object.values(merged).sort((a: any, b: any) => a.order - b.order));
      setLoading(false);
    }).catch(() => { setData([]); setLoading(false); });
  }, [config, specialty, viewMode, reportMonth]);

  const value = data.reduce((sum, d) => sum + (d.value || 0), 0);
  const display = `${config.prefix || ""}${value.toLocaleString()}${config.suffix || ""}`;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6 flex items-center justify-center h-40">
        <Loader2 className="w-6 h-6 animate-spin text-trofa-700" />
      </div>
    );
  }

  if (config.type === "kpi") {
    const Icon = config.metric?.toLowerCase().includes("fat") ? DollarSign : config.metric?.toLowerCase().includes("ocup") ? Activity : config.metric?.toLowerCase().includes("cons") ? Users : TrendingUpIcon;
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6 hover:shadow-sm transition-shadow">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 rounded-lg bg-trofa-50"><Icon className="w-5 h-5 text-trofa-700" /></div>
          <span className="text-sm text-slate-500">{config.title}</span>
        </div>
        <p className="text-2xl font-bold text-slate-900">{display}</p>
      </div>
    );
  }

  const chartHeight = config.type === "chart" ? 300 : 280;
  const isHorizontal = config.entity_level === "unit" || config.entity_level === "doctor";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-slate-900">{config.title}</h3>
        <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">{config.period_type}</span>
      </div>
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart data={data} layout={isHorizontal ? "vertical" : "horizontal"} margin={isHorizontal ? { left: 100 } : undefined}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={!isHorizontal} vertical={isHorizontal} />
            <XAxis type={isHorizontal ? "number" : "category"} dataKey={isHorizontal ? undefined : "label"} stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
            <YAxis type={isHorizontal ? "category" : "number"} dataKey={isHorizontal ? "label" : undefined} stroke="#64748b" fontSize={11} width={isHorizontal ? 100 : undefined} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} formatter={(v: any) => [`${config.prefix || ""}${Number(v).toLocaleString()}${config.suffix || ""}`, config.title]} />
            <Bar dataKey="value" fill={CHART_PALETTE[Math.floor(Math.random() * CHART_PALETTE.length)]} radius={isHorizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <p className="text-sm text-slate-400 py-8 text-center">No data available for this widget.</p>
      )}
    </div>
  );
}

function ComparisonBadge({ viewMode, currentValue, actualsTotal, prevValue }: any) {
  const primary = viewMode === "current" ? currentValue : actualsTotal;
  const compare = viewMode === "current" ? prevValue : currentValue;
  if (primary === 0 || compare === 0) return null;
  const pct = Math.abs(Math.round(((primary - compare) / compare) * 100));
  return (
    <span className={`text-sm font-medium ${primary >= compare ? "text-green-600" : "text-red-500"}`}>
      {primary >= compare ? "↑" : "↓"} {pct}%
    </span>
  );
}
